import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { useConnectionQuality } from '@/hooks/useConnectionQuality';

/**
 * Check if the browser is online
 */
export const isOnline = (): boolean => {
  return typeof navigator !== 'undefined' && navigator.onLine;
};

/**
 * Hook to track online/offline status with debounce
 */
export const useOnlineStatus = (debounceMs: number = 300) => {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const debounceTimerRef = useRef<number | null>(null);
  const toastIdRef = useRef<string | number | null>(null);

  const handleStatusChange = useCallback((online: boolean) => {
    // Clear any existing debounce timer
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Set a new timer
    debounceTimerRef.current = window.setTimeout(() => {
      setIsOnline(online);
      
      // Show toast notification for status change
      if (online) {
        // Dismiss any offline toast first
        if (toastIdRef.current !== null) {
          toast.dismiss(toastIdRef.current);
          toastIdRef.current = null;
        }
        
        toast.success('Connection restored', {
          description: 'You are back online',
          duration: 3000,
        });
      } else {
        // Only show the offline toast if we don't already have one
        if (toastIdRef.current === null) {
          toastIdRef.current = toast.error('Connection lost', {
            description: 'Please check your internet connection',
            duration: Infinity, // Keep showing until dismissed or connection restored
          });
        }
      }
    }, debounceMs);
  }, [debounceMs]);

  useEffect(() => {
    const handleOnline = () => handleStatusChange(true);
    const handleOffline = () => handleStatusChange(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      // Clear any pending debounce timer
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
      }
      
      // Dismiss any lingering toast
      if (toastIdRef.current !== null) {
        toast.dismiss(toastIdRef.current);
      }
    };
  }, [handleStatusChange]);

  return isOnline;
};

/**
 * Advanced hook for network status monitoring with detailed metrics
 */
export const useNetworkStatus = () => {
  const isOnline = useOnlineStatus();
  const { isSlowConnection, connectionType, getConnectionDetails } = useConnectionQuality();
  const [latency, setLatency] = useState<number | null>(null);
  const [latencyTrend, setLatencyTrend] = useState<'improving' | 'degrading' | 'stable'>('stable');
  const latencyHistoryRef = useRef<number[]>([]);
  
  // Periodically measure network latency when online
  useEffect(() => {
    if (!isOnline) return;
    
    const measureLatency = async () => {
      const start = performance.now();
      try {
        // Make a minimal request to measure latency
        const res = await fetch('/favicon.ico', { 
          method: 'HEAD',
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        if (res.ok) {
          const end = performance.now();
          const newLatency = end - start;
          
          // Update latency history (keep last 5 measurements)
          latencyHistoryRef.current.push(newLatency);
          if (latencyHistoryRef.current.length > 5) {
            latencyHistoryRef.current.shift();
          }
          
          // Calculate trend
          if (latencyHistoryRef.current.length >= 3) {
            const oldest = latencyHistoryRef.current[0];
            const newest = latencyHistoryRef.current[latencyHistoryRef.current.length - 1];
            const diff = newest - oldest;
            
            if (diff > 50) { // More than 50ms increase
              setLatencyTrend('degrading');
            } else if (diff < -50) { // More than 50ms decrease
              setLatencyTrend('improving');
            } else {
              setLatencyTrend('stable');
            }
          }
          
          setLatency(newLatency);
        }
      } catch (error) {
        console.error('Error measuring latency:', error);
      }
    };
    
    // Initial measurement
    measureLatency();
    
    // Set up interval for periodic measurements
    const interval = setInterval(measureLatency, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [isOnline]);

  return {
    isOnline,
    isSlowConnection,
    connectionType,
    connectionDetails: getConnectionDetails(),
    latency,
    latencyTrend
  };
};

/**
 * Adaptive retry backoff strategy for networks
 */
const calculateBackoff = (
  retry: number, 
  baseDelayMs: number = 1000, 
  options: { maxRetries?: number; maxDelayMs?: number; jitter?: boolean } = {}
): number => {
  const { maxRetries = 5, maxDelayMs = 30000, jitter = true } = options;
  
  if (retry >= maxRetries) return -1; // Signal to stop retrying
  
  // Exponential backoff: 2^retry * baseDelay
  let delay = Math.min(Math.pow(2, retry) * baseDelayMs, maxDelayMs);
  
  // Add jitter to prevent thundering herd
  if (jitter) {
    delay = delay * (0.5 + Math.random() * 0.5);
  }
  
  return delay;
};

/**
 * Handle retry logic for critical operations with adaptive backoff
 */
export const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelayMs: number = 1000,
  options: { 
    onRetry?: (attempt: number, delay: number, error: any) => void;
    shouldRetry?: (error: any, attempt: number) => boolean;
    maxDelayMs?: number;
    jitter?: boolean;
  } = {}
): Promise<T> => {
  const { 
    onRetry, 
    shouldRetry = (error) => isNetworkError(error),
    maxDelayMs = 10000,
    jitter = true
  } = options;
  
  let retries = 0;
  
  while (true) {
    try {
      return await operation();
    } catch (error) {
      retries++;
      
      if (retries > maxRetries || !shouldRetry(error, retries)) {
        throw error;
      }
      
      const delay = calculateBackoff(retries, baseDelayMs, {
        maxRetries,
        maxDelayMs,
        jitter
      });
      
      if (delay < 0) throw error; // Stop retrying
      
      if (onRetry) {
        onRetry(retries, delay, error);
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

/**
 * Check if the error is likely a network error
 */
export const isNetworkError = (error: any): boolean => {
  return (
    error?.message?.includes('network') ||
    error?.message?.includes('Failed to fetch') ||
    error?.message?.includes('Network request failed') ||
    error?.message?.includes('timeout') ||
    error?.code === 'ECONNABORTED' ||
    error?.name === 'AbortError' ||
    error?.name === 'NetworkError' ||
    error?.name === 'TimeoutError' ||
    // Supabase specific network errors
    error?.message?.includes('connection') ||
    error?.message?.includes('fetch failed')
  );
};

/**
 * Smart fetch with automatic retry, timeout, and error handling
 */
export const smartFetch = async <T>(
  url: string,
  options: RequestInit & {
    timeout?: number;
    retries?: number;
    baseRetryDelay?: number;
    parseJson?: boolean;
    abortSignal?: AbortSignal;
  } = {}
): Promise<T> => {
  const {
    timeout = 10000,
    retries = 3,
    baseRetryDelay = 1000,
    parseJson = true,
    abortSignal,
    ...fetchOptions
  } = options;
  
  // Create abort controller for this request (if not provided)
  const controller = abortSignal ? null : new AbortController();
  const signal = abortSignal || controller?.signal;
  
  // Set timeout if specified
  let timeoutId: number | null = null;
  if (timeout > 0 && controller) {
    timeoutId = window.setTimeout(() => {
      controller.abort('Request timeout');
    }, timeout);
  }
  
  try {
    const response = await retryOperation(
      async () => {
        const res = await fetch(url, {
          ...fetchOptions,
          signal,
        });
        
        if (!res.ok) {
          const error = new Error(`HTTP error ${res.status}: ${res.statusText}`);
          (error as any).status = res.status;
          (error as any).response = res;
          throw error;
        }
        
        return res;
      },
      retries,
      baseRetryDelay
    );
    
    if (parseJson) {
      return await response.json() as T;
    }
    
    return response as unknown as T;
  } finally {
    // Clean up timeout if it's still active
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }
};
